import { UuidNil } from "common/uuid";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap"
import EmailSideBar from "./EmailSideBar"
import { deleteEmail, getEmails, getEmailsBefore } from "store/actions/Emails";
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { RootState } from "store/store"
import WaitingDots from "components/WaitingDots"
import EmailRowInbox from "./EmailRowInbox"
import { GetRouterQuery } from "components/Utils/GetRouterQuery";
import EmailCompose from "./EmailCompose";
import PageMeta from "components/PageMeta";
import { EmailState } from 'store/reducers/Emails';
import { Email } from "models/Email";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { useEmailSearch } from "./useEmailSearch";


import MarkAsSpamButton from "./MarkAsSpamButton";

interface Props {
  emailState: EmailState;
}

interface Actions {
  deleteEmail: (email: Email) => Promise<unknown>;
  getEmails: () => void;
  getEmailsBefore: (date: number) => Promise<unknown>;
}

const EmailRowHeight = 50;
const EmailListFallbackHeight = 650;
const EmailListOverscan = 12;

export const EmailInbox: React.FC<Props & Actions> = ({ emailState, deleteEmail, getEmails, getEmailsBefore }) => {
  const { t, intlLocale } = useOfficeTranslation();

  const { box } = GetRouterQuery<{ box: string }>()
  const [nameKeyword, setNameKeyword] = useState(() => new URLSearchParams(window.location.search).get("q") ?? "")
  const searchActive = box !== "compose" && nameKeyword.trim() !== "";
  const showMailList = searchActive || emailState.getEmails().length > 0;
  const search = useEmailSearch(searchActive ? nameKeyword : "", box);
  const [loadingMore, setLoadingMore] = useState(false)
  const [deletingSelected, setDeletingSelected] = useState(false)
  const [selectedEmailIds, setSelectedEmailIds] = useState<string[]>([])
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(EmailListFallbackHeight)
  const listRef = useRef<HTMLUListElement | null>(null)
  const loadingMoreRef = useRef(false)
  const nearBottomRef = useRef(false)
  const previousListRef = useRef<{ emails: Email[]; box: string; keyword: string } | null>(null)

  useEffect(() => {
    if (!emailState.isFullyFetched()) {
      getEmails()
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const updateQuery = () => setNameKeyword(new URLSearchParams(window.location.search).get("q") ?? "");
    window.addEventListener("popstate", updateQuery);
    return () => window.removeEventListener("popstate", updateQuery);
  }, []);

  const fetchOlderEmails = useCallback(() => {
    if (searchActive || loadingMoreRef.current || !emailState.canFetchMoreOlder()) {
      return;
    }

    const oldest = emailState.getOldestFetchedDate();
    if (oldest === null) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);
    getEmailsBefore(oldest).finally(() => {
      loadingMoreRef.current = false;
      setLoadingMore(false)
    });
  }, [emailState, getEmailsBefore, searchActive]);

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }

    const updateViewportHeight = () => {
      setViewportHeight(listElement.clientHeight || EmailListFallbackHeight);
    };

    updateViewportHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateViewportHeight);
      return () => window.removeEventListener("resize", updateViewportHeight);
    }

    const resizeObserver = new ResizeObserver(updateViewportHeight);
    resizeObserver.observe(listElement);

    return () => resizeObserver.disconnect();
  }, [box, searchActive, showMailList]);

  useEffect(() => {
    setSelectedEmailIds([])
    nearBottomRef.current = false;
  }, [box, nameKeyword])


  const getThreadSizeUp = (id: string): number => {
    let size = 0;
    const threads = emailState.getThreads(id);
    for (let i = 0; i < threads.length; i++) {
      const newer = emailState.getEmailById(threads[i]);
      if (newer) {
        size += 1;
        size += getThreadSizeUp(threads[i])
      }
    }

    return size;
  }
  const getThreadSizeDown = (id: string): number => {
    let size = 0;
    const email = emailState.getEmailById(id);
    if (email) {
      size += 1;
      if (email.reply_to !== UuidNil) {
        size += getThreadSizeDown(email.reply_to)
      }
    }
    return size;
  }
  const hasNewerOfDirection = (dir: string, id: string): boolean => {
    const threads = emailState.getThreads(id);
    for (let i = 0; i < threads.length; i++) {
      const newer = emailState.getEmailById(threads[i]);
      if (!newer) {
        return false;
      }
      if (newer.direction === dir) {
        return true;
      }
      return hasNewerOfDirection(dir, threads[i])
    }
    return false;
  };

  const filteredMails = useMemo(() => {
    return emailState.getEmails()
      .filter(email => box !== "inbox" || email.direction === "in")
      .filter(email => box !== "starred" || email.starred)
      .filter(email => box !== "sent" || email.direction === "out" || email.direction === "")
      .sort((a, b) => b.date - a.date)
  }, [box, emailState]);

  const visibleMails = useMemo(() => {
    if (searchActive) return search.emails;
    return filteredMails.filter(email => {
      return !hasNewerOfDirection(box === "inbox" ? "in" : "out", email.id);
    }).filter((email, index, mails) => mails.findIndex(item => item.id === email.id) === index)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box, emailState, filteredMails, searchActive, search.emails]);

  useLayoutEffect(() => {
    const listElement = listRef.current;
    const previous = previousListRef.current;
    previousListRef.current = { emails: visibleMails, box, keyword: nameKeyword };
    if (!listElement) return;

    if (!previous || previous.box !== box || previous.keyword !== nameKeyword) {
      listElement.scrollTop = 0;
    } else if (previous.emails !== visibleMails && scrollTop > 0) {
      // Keep the same mail at the same pixel offset when the list changes.
      // Older pages normally append; polling or thread updates can shift rows above it.
      const anchorIndex = Math.floor(scrollTop / EmailRowHeight);
      const anchor = previous.emails[anchorIndex];
      const nextIndex = anchor ? visibleMails.findIndex(email => email.id === anchor.id) : -1;
      if (nextIndex >= 0) {
        listElement.scrollTop = nextIndex * EmailRowHeight + scrollTop % EmailRowHeight;
      }
    }

    if (listElement.scrollTop !== scrollTop) {
      setScrollTop(listElement.scrollTop);
    }
    // A newly appended page moves the bottom away without a scroll event.
    // Re-arm pagination here, including when the next wheel movement is large.
    if (listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight >= 500) {
      nearBottomRef.current = false;
    }
  }, [box, nameKeyword, visibleMails, scrollTop]);

  const startIndex = Math.min(Math.max(0, visibleMails.length - 1), Math.max(0, Math.floor(scrollTop / EmailRowHeight) - EmailListOverscan));
  const visibleRowCount = Math.ceil(viewportHeight / EmailRowHeight) + EmailListOverscan * 2;
  const endIndex = Math.min(visibleMails.length, startIndex + visibleRowCount);
  const renderedMails = visibleMails.slice(startIndex, endIndex);
  const topSpacerHeight = startIndex * EmailRowHeight;
  const bottomSpacerHeight = Math.max(0, (visibleMails.length - endIndex) * EmailRowHeight);

  const selectedEmails = selectedEmailIds
    .map(id => searchActive ? search.emails.find(email => email.id === id) ?? null : emailState.getEmailById(id))
    .filter((email): email is Email => email !== null);

  const toggleSelectedEmail = (email: Email) => {
    setSelectedEmailIds(current => current.includes(email.id)
      ? current.filter(id => id !== email.id)
      : [...current, email.id])
  }

  const deleteSelectedEmails = () => {
    if (selectedEmails.length === 0 || deletingSelected) {
      return;
    }

    setDeletingSelected(true);
    Promise.all(selectedEmails.map(email => deleteEmail(email)))
      .then(() => {
        search.removeEmails(selectedEmailIds);
        setSelectedEmailIds([]);
      })
      .finally(() => setDeletingSelected(false));
  }

  const formatSearchDate = (value: number) => new Date(value * 1000).toLocaleDateString(intlLocale, {day: "numeric", month: "long", year: "numeric"});

  return (
    <>
      <div className={`page-content ${box === "compose" ? "email-compose-page" : ""}`}>
        <PageMeta title={t("common.conversations")} />
        <Container fluid>
          <Row>
            <Col xs="12">
              <EmailSideBar box={box} unread={emailState.getEmails()
                .filter(email => email.direction === "in" && !email.IsRead()).length} />
              <div className="email-rightbar mb-3">
                {
                  box !== "compose" ?
                    <>
                      <div className="app-search mb-3" style={{ marginLeft: "5px", marginRight: "5px" }}>
                        <div className="position-relative">
                          <input type="text"
                            name="search" autoComplete={'off'} id="search" key="search" className="form-control" value={nameKeyword}
                            onChange={(e) => {
                              const value = e.target.value;
                              setNameKeyword(value);
                              const url = new URL(window.location.href);
                              if (value) url.searchParams.set('q', value);
                              else url.searchParams.delete('q');
                              window.history.replaceState({}, '', url);
                            }}
                            placeholder={t("common.search")} />
                          <span className="mdi mdi-magnify"></span>
                        </div>
                      </div>
                      <Card style={{ marginLeft: "5px", marginRight: "5px" }}>
                        <CardBody>
                          <div style={{ display: "flex", alignItems: "left", justifyContent: "space-between", marginBottom: "10px" }}>
                            <p className="text-muted text-uppercase fw-semibold" style={{ fontSize: "13px" }}>{box}</p>
                            <div>

                              {selectedEmails.length > 0 &&
                                  <>
                                    <span className="text-muted" style={{margin:"5px"}}>{selectedEmails.length} {t("email.emailInbox.selected")}</span>
                                    <MarkAsSpamButton emails={selectedEmails} disabled={deletingSelected} onMoved={id => {
                                      search.removeEmails([id]);
                                      setSelectedEmailIds(current => current.filter(selected => selected !== id));
                                    }} />
                                    <Button
                                        color="danger"
                                        size="sm"
                                        disabled={deletingSelected}
                                        onClick={deleteSelectedEmails}
                                    >
                                      <i className="mdi mdi-delete" /> {t("common.delete")}
                                    </Button>
                                  </>
                              }
                            </div>
                          </div>
                          {!showMailList ?
                            <div style={{ textAlign: "center" }}>
                              <WaitingDots />
                            </div> :
                            <ul
                              ref={listRef}
                              className="message-list email-message-list"
                              style={{ maxHeight: "calc(100vh - 330px)", overflowY: "auto", marginBottom: 0 }}
                              onScroll={(event) => {
                                const listElement = event.currentTarget;
                                setScrollTop(listElement.scrollTop);
                                const nearBottom = listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight < 500;

                                if (!nearBottom) {
                                  nearBottomRef.current = false;
                                } else if (!nearBottomRef.current) {
                                  nearBottomRef.current = true;
                                  fetchOlderEmails();
                                }
                              }}
                            >
                              <li className="email-list-spacer" aria-hidden="true" style={{ height: topSpacerHeight, padding: 0, pointerEvents: "none" }} />
                              {renderedMails
                                .map((email) => {
                                  if (searchActive) {
                                    return <EmailRowInbox key={email.id} email={email} hideDirection={true} threadSize={1} selectable={true} selected={selectedEmailIds.includes(email.id)} onToggleSelect={toggleSelectedEmail} onStarChange={search.updateEmail} />;
                                  }

                                  return <EmailRowInbox key={email.id} email={email} hideDirection={true} threadSize={1 + getThreadSizeUp(email.id) + getThreadSizeDown(email.reply_to)} selectable={true} selected={selectedEmailIds.includes(email.id)} onToggleSelect={toggleSelectedEmail} />
                                })
                              }
                              <li className="email-list-spacer" aria-hidden="true" style={{ height: bottomSpacerHeight, padding: 0, pointerEvents: "none" }} />
                            </ul>
                          }
                          {searchActive &&
                            <div className="email-search-state">
                              {!search.loading && !search.error && visibleMails.length === 0 && <p className="mb-2">{t("email.emailInbox.noSearchResults")}</p>}
                              {search.page && <p className="email-search-caption" role="status">
                                {t("email.emailInbox.resultsUntil", {date: formatSearchDate(search.page.searched_until)})}
                              </p>}
                              {search.loading && <div role="status" aria-label={t("email.emailInbox.searching")}><WaitingDots /></div>}
                              {search.error && <div role="alert" className="alert alert-danger d-flex flex-wrap align-items-center gap-2">
                                <span>{t("email.emailInbox.searchFailed")}</span>
                                <Button color="danger" outline onClick={search.retry} style={{minHeight: 44}}>{t("email.emailInbox.retrySearch")}</Button>
                              </div>}
                              {!search.loading && !search.error && search.page?.window_complete && search.page.older_cursor &&
                                <Button color="link" className="email-search-more" onClick={search.searchOlder}>
                                  {t("email.emailInbox.searchFurther")}
                                  <i className="bx bx-chevron-down" aria-hidden="true" />
                                </Button>}
                            </div>}
                          {loadingMore &&
                            <div
                              role="status"
                              aria-label={t("email.emailInbox.loadingOlder")}
                              style={{ textAlign: "center" }}
                            >
                              <WaitingDots />
                            </div>
                          }
                        </CardBody>
                      </Card>
                    </>
                    :
                    <EmailCompose />
                }
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    emailState: state.Emails
  };
};


const mapDispatchToProps = (dispatch: any) => {
  return bindActionCreators({ deleteEmail, getEmails, getEmailsBefore }, dispatch);
};


export default connect(mapStateToProps, mapDispatchToProps)(EmailInbox);
